// Admin — Invite Friends (PRD §3.11).
// Add/edit friends via modal; list below the button.

import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { api, ApiError } from "../../lib/api";
import type { Friend } from "../../lib/types";
import { generatePassword, generateUsername } from "../../lib/funNames";
import { EMOJIS } from "../../lib/emojis";
import { timeAgo } from "../../lib/time";
import { AppHeader } from "../../components/AppHeader";
import { AdminNav } from "../../components/AdminNav";
import { Field } from "../../components/Field";
import { Button } from "../../components/Button";

const SITE_URL = "https://citrusdala.com";

function waMessage(name: string, username: string, password: string): string {
  return (
    `Hi ${name}! Messy (Lionel) has invited you to his new Citrus-Dala blog 🍊 ` +
    `Your login is ${username} and password ${password}. Come see: ${SITE_URL}`
  );
}

function waLink(name: string, username: string, password: string): string {
  // Deep-link straight into the WhatsApp app's "share to a chat" picker with the
  // message pre-filled — Messy just taps the friend. Using the app scheme skips
  // WhatsApp's wa.me web page (no "download WhatsApp" / contact-list-only detour).
  return `whatsapp://send?text=${encodeURIComponent(waMessage(name, username, password))}`;
}

// ── Modal ─────────────────────────────────────────────────────────────────────

type ModalState =
  | { mode: "add" }
  | { mode: "edit"; friend: Friend };

interface Credentials { handle: string; password: string; realName: string }

function FriendModal({
  state,
  onClose,
  onSaved,
}: {
  state: ModalState;
  onClose: () => void;
  onSaved: (f: Friend) => void;
}) {
  const isEdit = state.mode === "edit";
  const existing = isEdit ? state.friend : null;

  const [realName, setRealName] = useState(existing?.realName ?? "");
  const [emoji, setEmoji] = useState(existing?.emoji ?? "🍊");
  const [handle, setHandle] = useState(existing?.handle ?? generateUsername());
  // Editing: start blank — a freshly-rolled password here would look like "the current
  // password" when it's really just a reset candidate nobody's saved yet. Adding: needs
  // a real password up front to create the account.
  const [password, setPassword] = useState(isEdit ? "" : generatePassword());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creds, setCreds] = useState<Credentials | null>(null);
  const [confirmResend, setConfirmResend] = useState(false);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setSaving(true);
    try {
      if (isEdit && existing) {
        // "Save changes" never touches the password — that's the resend button's job.
        const { friend } = await api.updateFriend(existing.id, {
          realName: realName.trim(), emoji, handle,
        });
        onSaved(friend);
        onClose();
      } else {
        const { friend } = await api.createFriend({
          realName: realName.trim(), emoji, handle, password,
        });
        onSaved(friend);
        setCreds({ handle: friend.handle, password, realName: friend.realName });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong — try again.");
    } finally {
      setSaving(false);
    }
  }

  // Reset a friend's password to the one in the field and hand back a WhatsApp
  // share — always available, so Messy can resend a login whenever a friend forgets.
  // No window.confirm() here: it silently no-ops inside an installed PWA / in-app
  // webview (that's what made this button feel "dead"). The credentials panel that
  // opens next is the confirmation — it shows the new password before sharing.
  async function handleResend() {
    if (!existing || saving) return;
    if (password.trim().length < 3) {
      setError("That password's a bit short — roll another 🎲");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const { friend } = await api.updateFriend(existing.id, {
        realName: realName.trim(), emoji, handle, password,
      });
      onSaved(friend);
      setCreds({ handle: friend.handle, password, realName: friend.realName });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't reset — try again.");
    } finally {
      setSaving(false);
    }
  }

  const overlayRef = useRef<HTMLDivElement>(null);

  return (
    <>
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-2xl border-2 border-ink/10 bg-paper shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b-2 border-ink/10 px-5 py-4">
          <h2 className="text-lg font-bold text-ink">
            {creds
              ? isEdit
                ? "Password reset!"
                : "Friend added!"
              : isEdit
                ? `Edit ${existing!.realName}`
                : "Add a friend"}
          </h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-paper-deep hover:text-ink"
          >
            ×
          </button>
        </div>

        <div className="max-h-[80vh] overflow-y-auto px-5 py-5">
          {/* After-add credentials panel */}
          {creds ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-xl border-2 border-citrus/40 bg-citrus/10 p-4">
                <p className="text-sm font-semibold text-ink">Login details for {creds.realName}</p>
                <p className="mt-2 font-mono text-sm text-ink-soft">
                  Username: <span className="font-bold text-ink">{creds.handle}</span>
                </p>
                <p className="font-mono text-sm text-ink-soft">
                  Password: <span className="font-bold text-ink">{creds.password}</span>
                </p>
                <p className="mt-2 text-xs text-ink-muted">
                  Share this now — the password is shown once and stored encrypted.
                </p>
              </div>
              <a
                href={waLink(creds.realName, creds.handle, creds.password)}
                className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 font-semibold text-white shadow-[2px_2px_0_0_rgba(0,0,0,0.25)]"
              >
                Share on WhatsApp
              </a>
              {/* Desktop failsafe: if WhatsApp isn't installed here, the app link
                  above does nothing — so offer a copy that pastes anywhere (incl.
                  WhatsApp Web, email, SMS). */}
              <CopyMessageButton
                text={waMessage(creds.realName, creds.handle, creds.password)}
              />
              <p className="-mt-1 text-center text-xs text-ink-muted">
                No WhatsApp on this device? Copy the message and paste it anywhere.
              </p>
              <Button variant="ghost" onClick={onClose} className="w-full">
                Done
              </Button>
            </div>
          ) : (
            /* Add / Edit form */
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {error ? (
                <p className="rounded-lg border-2 border-love/30 bg-love/5 px-3 py-2 text-sm text-love">
                  {error}
                </p>
              ) : null}

              <Field
                label="Real name"
                value={realName}
                onChange={(e) => setRealName(e.target.value)}
                placeholder="e.g. Sarah"
                required
              />

              {/* Emoji picker */}
              <div>
                <span className="mb-1.5 block text-sm font-semibold text-ink-soft">Emoji</span>
                <div className="flex flex-wrap gap-1.5">
                  {EMOJIS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setEmoji(e)}
                      className={
                        "flex h-9 w-9 items-center justify-center rounded-md border-2 text-lg " +
                        (emoji === e ? "border-ink bg-paper-deep" : "border-transparent hover:border-ink/20")
                      }
                      aria-pressed={emoji === e}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>

              <DiceField
                label="Username"
                value={handle}
                onChange={setHandle}
                onRoll={() => setHandle(generateUsername())}
              />

              {!isEdit && (
                <DiceField
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  onRoll={() => setPassword(generatePassword())}
                />
              )}

              {isEdit && (
                <div className="rounded-xl border-2 border-ink/15 bg-paper-deep/40 p-3">
                  <DiceField
                    label="Password"
                    value={password}
                    onChange={setPassword}
                    onRoll={() => setPassword(generatePassword())}
                    placeholder="Roll 🎲 for a new password"
                  />
                  <p className="mt-1.5 text-xs text-ink-muted">
                    Friend forgot their login? Roll or type a password, then resend it — works any time,
                    even if nothing else changed. Their old password stops working.
                  </p>
                  <button
                    type="button"
                    onClick={() => setConfirmResend(true)}
                    disabled={saving}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 font-semibold text-white shadow-[2px_2px_0_0_rgba(0,0,0,0.25)] disabled:opacity-60"
                  >
                    Reset &amp; Resend on WhatsApp
                  </button>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <Button variant="ghost" type="button" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" loading={saving} className="flex-1">
                  {isEdit ? "Save changes" : "Add friend"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>

    {confirmResend ? (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 p-6 backdrop-blur-sm"
        onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmResend(false); }}
      >
        <div className="w-full max-w-sm overflow-hidden rounded-2xl border-2 border-ink/10 bg-paper shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
          <div className="px-5 py-5">
            <h2 className="text-lg font-bold text-ink">Save and send new login?</h2>
            <p className="mt-1 text-sm text-ink-muted">
              The username and password haven't been saved yet. Saving resets{" "}
              {existing?.realName ?? "their"}'s password — their old one stops working — then opens
              WhatsApp to share it.
            </p>
            <div className="mt-5 flex gap-3">
              <Button variant="ghost" type="button" onClick={() => setConfirmResend(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                type="button"
                loading={saving}
                onClick={() => { setConfirmResend(false); void handleResend(); }}
                className="flex-1"
              >
                Save &amp; send
              </Button>
            </div>
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function AdminFriends() {
  const { setPreviewPublic } = useAuth();
  useEffect(() => setPreviewPublic(false), [setPreviewPublic]);

  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Friend | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [sort, setSort] = useState<"name" | "seen">("seen");

  const sortedFriends = useMemo(() => {
    const arr = [...friends];
    if (sort === "name") {
      arr.sort((a, b) => a.realName.localeCompare(b.realName));
    } else {
      // Most-recently-seen first; friends who've never logged in sink to the bottom.
      arr.sort((a, b) => {
        const at = a.lastLoginAt ? Date.parse(a.lastLoginAt) : -Infinity;
        const bt = b.lastLoginAt ? Date.parse(b.lastLoginAt) : -Infinity;
        return bt - at;
      });
    }
    return arr;
  }, [friends, sort]);

  async function load() {
    setLoading(true);
    try {
      const { friends } = await api.friends();
      setFriends(friends);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load friends.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  function handleSaved(f: Friend) {
    setFriends((prev) => {
      const idx = prev.findIndex((p) => p.id === f.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = f;
        return next;
      }
      return [f, ...prev];
    });
  }

  // Delete is driven by the confirmation modal (no window.confirm — it no-ops in an
  // installed PWA / in-app webview, which is why the button felt dead).
  async function doRemove() {
    const friend = confirmRemove;
    if (!friend) return;
    setRemoveError(null);
    setBusyId(friend.id);
    try {
      await api.deleteFriend(friend.id);
      setFriends((prev) => prev.filter((f) => f.id !== friend.id));
      setConfirmRemove(null);
    } catch (err) {
      setRemoveError(err instanceof ApiError ? err.message : "Couldn't remove that friend.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <div className="min-h-dvh bg-paper">
        <AppHeader />
        <main className="mx-auto w-full max-w-xl px-5 py-6">
          <AdminNav />

          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-ink">Friends</h1>
            <button
              onClick={() => setModal({ mode: "add" })}
              className="inline-flex items-center gap-1.5 rounded-md border-2 border-ink bg-paper px-4 py-2 text-sm font-semibold text-citrus-deep shadow-[2px_2px_0_0_var(--color-ink)] transition-transform active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
            >
              Invite A Friend <span aria-hidden>🙋</span>
            </button>
          </div>

          {error ? (
            <p className="mt-4 rounded-lg border-2 border-love/30 bg-love/5 px-3 py-2 text-sm text-love">
              {error}
            </p>
          ) : null}

          {/* Sort control */}
          {!loading && friends.length > 0 ? (
            <div className="mt-6 flex items-center justify-between">
              <span className="text-sm text-ink-muted">
                {friends.length} {friends.length === 1 ? "friend" : "friends"}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-ink-muted">Sort</span>
                <div className="flex overflow-hidden rounded-lg border-2 border-ink/20">
                  {([
                    { key: "name", label: "Name" },
                    { key: "seen", label: "Last seen" },
                  ] as const).map((opt, i) => (
                    <button
                      key={opt.key}
                      onClick={() => setSort(opt.key)}
                      className={
                        (i > 0 ? "border-l-2 border-ink/20 " : "") +
                        "px-3 py-1 text-xs font-semibold transition-colors " +
                        (sort === opt.key ? "bg-ink/15 text-ink" : "text-ink-soft hover:text-ink")
                      }
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {/* Friends list */}
          <div className="mt-4">
            {loading ? (
              <div className="flex justify-center py-10">
                <span className="h-6 w-6 animate-spin rounded-full border-[3px] border-citrus/30 border-t-citrus" />
              </div>
            ) : friends.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-ink/20 px-6 py-14 text-center">
                <p className="font-display text-2xl text-ink">No friends yet</p>
              </div>
            ) : (
              <ul className="divide-y divide-ink/10 rounded-xl border-2 border-ink/15 bg-white">
                {sortedFriends.map((f) => (
                  <li key={f.id} className="flex items-center gap-3 px-4 py-3">
                    <span className="text-2xl" aria-hidden>{f.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-ink">{f.realName}</p>
                      <p className="truncate font-mono text-xs text-ink-muted">
                        {f.handle}
                        {" · "}
                        <span className={f.lastLoginAt ? "text-citrus-deep" : ""}>
                          {f.lastLoginAt ? `last seen ${timeAgo(f.lastLoginAt)}` : "never logged in"}
                        </span>
                      </p>
                    </div>
                    <button
                      onClick={() => setModal({ mode: "edit", friend: f })}
                      className="rounded-lg border-2 border-ink/20 px-2.5 py-1.5 text-xs font-semibold text-ink-soft hover:border-ink"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => { setRemoveError(null); setConfirmRemove(f); }}
                      className="rounded-lg px-2 py-1.5 text-xs font-semibold text-love/70 hover:text-love"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </main>
      </div>

      {modal ? (
        <FriendModal
          state={modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      ) : null}

      {confirmRemove ? (
        <ConfirmRemoveModal
          friend={confirmRemove}
          busy={busyId === confirmRemove.id}
          error={removeError}
          onCancel={() => { setConfirmRemove(null); setRemoveError(null); }}
          onConfirm={() => void doRemove()}
        />
      ) : null}
    </>
  );
}

// ── Confirm-remove modal ────────────────────────────────────────────────────────

function ConfirmRemoveModal({
  friend, busy, error, onCancel, onConfirm,
}: {
  friend: Friend;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape" && !busy) onCancel(); }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onCancel, busy]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === overlayRef.current && !busy) onCancel(); }}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-2xl border-2 border-ink/10 bg-paper shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
        <div className="px-5 py-5">
          <div className="flex items-start gap-3">
            <span className="text-3xl" aria-hidden>{friend.emoji}</span>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-ink">Remove {friend.realName}?</h2>
              <p className="mt-1 text-sm text-ink-muted">
                <span className="font-mono">{friend.handle}</span> will lose access to the blog
                right away. You can always invite them again later.
              </p>
            </div>
          </div>

          {error ? (
            <p className="mt-4 rounded-lg border-2 border-love/30 bg-love/5 px-3 py-2 text-sm text-love">
              {error}
            </p>
          ) : null}

          <div className="mt-5 flex gap-3">
            <Button variant="ghost" type="button" onClick={onCancel} disabled={busy} className="flex-1">
              Cancel
            </Button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-ink bg-love px-5 py-3 font-semibold text-white shadow-[3px_3px_0_0_var(--color-ink)] transition-transform active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60"
            >
              {busy ? "Removing…" : "Remove"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Copy-message button (desktop WhatsApp failsafe) ─────────────────────────────

function CopyMessageButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older/locked-down browsers: fall back to a hidden textarea + execCommand.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* give up quietly */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      className="flex items-center justify-center gap-2 rounded-xl border-2 border-ink/25 bg-paper-deep px-4 py-3 font-semibold text-ink-soft transition-colors hover:border-ink hover:text-ink"
    >
      {copied ? "Copied ✓" : "Copy message"}
    </button>
  );
}

// ── Dice field ─────────────────────────────────────────────────────────────────

function DiceField({
  label, value, onChange, onRoll, placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onRoll: () => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-ink-soft">{label}</span>
      <div className="flex gap-2">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="w-full rounded-xl border-2 border-ink/25 bg-paper-deep px-3 py-2 font-mono text-ink focus:border-citrus focus:outline-none"
        />
        <button
          type="button"
          onClick={onRoll}
          title="Roll a new one"
          className="shrink-0 rounded-xl border-2 border-ink/25 px-3 text-lg hover:border-ink"
        >
          🎲
        </button>
      </div>
    </label>
  );
}
